// Data seam: Playground dispatch to real clients (POST /api/sphinx/playground/
// runs) and the per-tab polling that tracks those runs to completion. One
// dispatched run per sent message; each run is polled via GET /runs/:id every
// 2s until the run and all its iterations are terminal.
import { useCallback, useEffect, useRef, useState } from "react";
import {
  sphinxApi,
  type SphinxIteration,
  type SphinxRun,
} from "@/lib/sphinx-api";
import {
  DEFAULT_YARN_ORG,
  YARN_ENVS,
  YARN_ORGS,
  type SphinxModel,
  type TargetId,
  type YarnEnv,
  type YarnOrg,
} from "@/shared/sphinx";

const POLL_MS = 2000;

export interface PlaygroundRunInput {
  prompt: string;
  targets: TargetId[];
  /**
   * Explicit connector override for this send; omitted = each client runs with
   * its own attached set (`targets.server_ids`). The Playground passes it only
   * when the user edited the selection for this chat.
   */
  serverIds?: string[];
  /** Selected connector NAMES; resolved to row ids at dispatch when
   *  `serverIds` is empty (the Playground holds names, not ids). */
  serverNames?: string[];
  yarnEnv?: YarnEnv;
  yarnOrg?: YarnOrg;
  /**
   * One model per client, from that client's own column picker. The server
   * drops a pick whose provider family does not match the client
   * (`modelOverridesFor`), so a client with no valid entry keeps its
   * configured model.
   */
  models?: Partial<Record<TargetId, SphinxModel>>;
  /**
   * The Playground conversation this send belongs to: the server upserts the
   * session row so the conversation reaches the Sessions rail at dispatch
   * time, and records the lineup it was held with.
   */
  chatSessionId?: string;
  /** Full Playground lineup for this send: "direct" and/or target ids. */
  lineup?: string[];
}

export interface PlaygroundRun {
  /** Client-side key; equals the server run id once dispatch succeeds. */
  key: string;
  runId: string | null;
  suiteId: string | null;
  prompt: string;
  targets: TargetId[];
  createdAt: number;
  /** Latest poll of GET /runs/:id (null until the first poll lands). */
  run: (SphinxRun & { iterations: SphinxIteration[] }) | null;
  /** Dispatch failure (the run never started) or the latest poll error. */
  error: string | null;
}

/** POST /playground/runs. */
export const createPlaygroundRun = (input: PlaygroundRunInput) =>
  sphinxApi.playground.createRun(input);

/**
 * `yarn-<env>-<org>` → (env, org); the legacy env-wide `yarn-<env>` → the
 * default org. Non-Yarn names → null. Mirrors the server's connector-name
 * grammar (server/queue/enqueue.ts).
 */
export function parseYarnConnectorName(
  name: string
): { yarnEnv: YarnEnv; yarnOrg: YarnOrg } | null {
  const m = /^yarn-([a-z]+)(?:-([a-z]+))?$/.exec(name);
  if (!m) return null;
  if (!(YARN_ENVS as readonly string[]).includes(m[1])) return null;
  const yarnEnv = m[1] as YarnEnv;
  if (!m[2]) return { yarnEnv, yarnOrg: DEFAULT_YARN_ORG };
  if (!(YARN_ORGS as readonly string[]).includes(m[2])) return null;
  return { yarnEnv, yarnOrg: m[2] as YarnOrg };
}

const TERMINAL = new Set(["complete", "canceled", "failed"]);

export const isPlaygroundRunSettled = (entry: PlaygroundRun): boolean => {
  if (entry.runId === null) return true; // dispatch failed; nothing to poll
  if (!entry.run) return false;
  return (
    TERMINAL.has(entry.run.status) &&
    entry.run.iterations.every((it) => TERMINAL.has(it.status))
  );
};

/**
 * Per-tab list of dispatched playground runs. `dispatch` fires the POST and
 * appends an entry (a failed dispatch appends an error entry so the column
 * can say what happened); a 2s interval refreshes every unsettled entry.
 */
export function usePlaygroundRuns(): {
  runs: PlaygroundRun[];
  dispatch: (input: PlaygroundRunInput) => Promise<void>;
  clear: () => void;
  /**
   * Replace the columns with a reopened chat's persisted dispatches. Entries
   * arrive detail-less (`run: null`), so the 2s poll fetches each one's
   * current state — and keeps polling any run that is still in flight, which
   * is how a chat switched away from and back mid-run shows live status.
   */
  hydrate: (chatSessionId: string) => Promise<void>;
} {
  const [runs, setRuns] = useState<PlaygroundRun[]>([]);
  const runsRef = useRef(runs);
  runsRef.current = runs;
  const seq = useRef(0);

  const dispatch = useCallback(async (input: PlaygroundRunInput) => {
    const key = `playground-run-${++seq.current}`;
    const base: PlaygroundRun = {
      key,
      runId: null,
      suiteId: null,
      prompt: input.prompt,
      targets: input.targets,
      createdAt: Date.now(),
      run: null,
      error: null,
    };
    setRuns((prev) => [...prev, base]);
    try {
      // Names are explicit intent too: resolve them to ids when the id map
      // hadn't loaded at send time. Only when the caller passed neither does
      // the dispatch omit the field (each client uses its own attachment).
      let serverIds = input.serverIds;
      if (!serverIds?.length && input.serverNames?.length) {
        const rows = await sphinxApi.servers.list();
        serverIds = input.serverNames
          .map((name) => rows.find((row) => row.name === name)?.id)
          .filter((id): id is string => Boolean(id));
      }
      const created = await createPlaygroundRun({
        prompt: input.prompt,
        targets: input.targets,
        serverIds,
        yarnEnv: input.yarnEnv,
        yarnOrg: input.yarnOrg,
        models: input.models,
        chatSessionId: input.chatSessionId,
        lineup: input.lineup,
      });
      setRuns((prev) =>
        prev.map((r) =>
          r.key === key
            ? { ...r, key: created.runId, runId: created.runId, suiteId: created.suiteId }
            : r
        )
      );
    } catch (error) {
      setRuns((prev) =>
        prev.map((r) =>
          r.key === key
            ? { ...r, error: error instanceof Error ? error.message : String(error) }
            : r
        )
      );
    }
  }, []);

  const clear = useCallback(() => setRuns([]), []);

  const hydrateSeq = useRef(0);
  const hydrate = useCallback(async (chatSessionId: string) => {
    const ticket = ++hydrateSeq.current;
    try {
      const rows = await sphinxApi.playground.listRunsForChat(chatSessionId);
      if (hydrateSeq.current !== ticket) return; // a newer switch superseded us
      setRuns(
        rows.map((row) => ({
          key: row.runId,
          runId: row.runId,
          suiteId: row.suiteId,
          prompt: row.prompt,
          targets: row.targets,
          createdAt: Date.parse(row.createdAt) || Date.now(),
          run: null,
          error: null,
        }))
      );
    } catch {
      // Leave the columns as the caller set them (cleared); the chat still
      // opens. The runs remain reachable from the Queue page.
    }
  }, []);

  const anyLive = runs.some((r) => !isPlaygroundRunSettled(r));
  useEffect(() => {
    if (!anyLive) return;
    let cancelled = false;
    const tick = async () => {
      const pending = runsRef.current.filter(
        (r) => r.runId !== null && !isPlaygroundRunSettled(r)
      );
      for (const entry of pending) {
        try {
          const run = await sphinxApi.runs.get(entry.runId!);
          if (cancelled) return;
          setRuns((prev) =>
            prev.map((r) => (r.key === entry.key ? { ...r, run, error: null } : r))
          );
        } catch (error) {
          if (cancelled) return;
          setRuns((prev) =>
            prev.map((r) =>
              r.key === entry.key
                ? { ...r, error: error instanceof Error ? error.message : String(error) }
                : r
            )
          );
        }
      }
    };
    void tick();
    const timer = setInterval(() => void tick(), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [anyLive]);

  return { runs, dispatch, clear, hydrate };
}
